import { type ComponentProps, type ReactNode, useLayoutEffect, useRef } from 'react';

/** «6,250» у полі: розряди читаються з першого погляду, а в стані лишається число. */
const display = (n: number): string => (n > 0 ? n.toLocaleString('en-US') : '');
const parse = (raw: string): number => Number(raw.replace(/\D/g, '').slice(0, 9)) || 0;

/**
 * Поле суми в цілих доларах з групуванням розрядів.
 *
 * Коми «6,250» з'являються й зникають під час введення, тож поле пам'ятає,
 * скільки ЦИФР стояло перед курсором, і після переформатування ставить його
 * туди ж — інакше при правці в середині числа курсор щоразу стрибав би в кінець.
 */
export function UsdAmountInput({
	value,
	onValueChange,
	...props
}: Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type' | 'inputMode'> & {
	value: number;
	onValueChange: (value: number) => void;
}): ReactNode {
	const ref = useRef<HTMLInputElement>(null);
	const caretDigits = useRef<number | null>(null);

	useLayoutEffect(() => {
		const el = ref.current;
		const digits = caretDigits.current;
		if (!el || digits === null) return;
		caretDigits.current = null;
		let pos = 0;
		for (let seen = 0; pos < el.value.length && seen < digits; pos += 1) {
			if (/\d/.test(el.value.charAt(pos))) seen += 1;
		}
		el.setSelectionRange(pos, pos);
	});

	return (
		<input
			{...props}
			ref={ref}
			type="text"
			inputMode="numeric"
			autoComplete="off"
			value={display(value)}
			onChange={(e) => {
				const { value: raw, selectionStart } = e.target;
				caretDigits.current = raw.slice(0, selectionStart ?? raw.length).replace(/\D/g, '').length;
				onValueChange(parse(raw));
			}}
		/>
	);
}
